import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, Download, Eye, Loader2, Calendar, TrendingUp, 
  Building2, Trash2, RefreshCw, FileBarChart, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPanel() {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("weekly");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [viewingReport, setViewingReport] = useState(null);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [reportsRes, campaignsRes, brandingRes] = await Promise.all([
        axios.get(`${API}/reports/list`, { headers }),
        axios.get(`${API}/campaigns/`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/reports/branding`, { headers })
      ]);

      setReports(reportsRes.data.reports || []);
      setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : []);
      setBranding(brandingRes.data.branding);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      let endpoint = "";
      let payload = {};

      switch (selectedReportType) {
        case "campaign":
          if (!selectedCampaign) {
            toast.error("Please select a campaign");
            setGenerating(false);
            return;
          }
          endpoint = `${API}/reports/campaign`;
          payload = { campaign_id: selectedCampaign };
          break;
        case "weekly":
          endpoint = `${API}/reports/weekly`;
          payload = {};
          break;
        case "monthly":
          endpoint = `${API}/reports/monthly`;
          payload = {};
          break;
        default:
          return;
      }

      const response = await axios.post(endpoint, payload, { headers });
      
      if (response.data.success) {
        toast.success(`${selectedReportType.charAt(0).toUpperCase() + selectedReportType.slice(1)} report generated!`);
        fetchData(); // Refresh reports list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const viewReport = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingReport(response.data.report);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  const deleteReport = async (reportId) => {
    if (!confirm("Delete this report?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const downloadReport = async (report) => {
    try {
      // First fetch the full report data
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/${report.report_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullReport = response.data.report;
      
      // Generate a beautifully formatted HTML report
      const reportBranding = fullReport.branding || branding || {};
      const primaryColor = reportBranding.primary_color || '#00E5FF';
      const companyName = reportBranding.company_name || 'YASNO Marketing';
      
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${fullReport.report_type?.charAt(0).toUpperCase() + fullReport.report_type?.slice(1)} Report - ${companyName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      background: #ffffff;
      color: #1a1a2e;
      line-height: 1.6;
      padding: 40px;
    }
    
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 30px;
      border-bottom: 3px solid ${primaryColor};
      margin-bottom: 40px;
    }
    
    .logo {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .logo-icon {
      width: 50px;
      height: 50px;
      background: ${primaryColor};
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 700;
      font-size: 24px;
    }
    
    .company-name {
      font-size: 24px;
      font-weight: 700;
      color: #1a1a2e;
    }
    
    .report-meta {
      text-align: right;
      color: #666;
    }
    
    .report-type {
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: ${primaryColor};
      font-weight: 600;
    }
    
    .report-date {
      font-size: 13px;
      margin-top: 4px;
    }
    
    h1 {
      font-size: 28px;
      color: #1a1a2e;
      margin-bottom: 30px;
    }
    
    h2 {
      font-size: 18px;
      color: #1a1a2e;
      margin: 30px 0 15px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid #eee;
    }
    
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      margin: 20px 0;
    }
    
    .summary-card {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 20px;
      text-align: center;
    }
    
    .summary-value {
      font-size: 32px;
      font-weight: 700;
      color: ${primaryColor};
    }
    
    .summary-label {
      font-size: 12px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 5px;
    }
    
    .period-box {
      background: #f8f9fa;
      border-left: 4px solid ${primaryColor};
      padding: 15px 20px;
      margin: 20px 0;
      border-radius: 0 8px 8px 0;
    }
    
    .period-label {
      font-size: 12px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .period-value {
      font-size: 16px;
      font-weight: 500;
      margin-top: 5px;
    }
    
    .campaign-card {
      background: #ffffff;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 20px;
      margin: 15px 0;
    }
    
    .campaign-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }
    
    .campaign-name {
      font-size: 16px;
      font-weight: 600;
    }
    
    .platform-badges {
      display: flex;
      gap: 8px;
    }
    
    .platform-badge {
      background: ${primaryColor}20;
      color: ${primaryColor};
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 500;
      text-transform: capitalize;
    }
    
    .metrics-row {
      display: flex;
      gap: 30px;
    }
    
    .metric {
      font-size: 14px;
    }
    
    .metric-label {
      color: #666;
    }
    
    .metric-value {
      font-weight: 600;
      color: ${primaryColor};
    }
    
    .highlights-list, .recommendations-list, .insights-list {
      list-style: none;
      padding: 0;
    }
    
    .highlights-list li, .recommendations-list li, .insights-list li {
      padding: 10px 0;
      padding-left: 25px;
      position: relative;
      border-bottom: 1px solid #f0f0f0;
    }
    
    .highlights-list li:before {
      content: "•";
      color: ${primaryColor};
      font-weight: bold;
      position: absolute;
      left: 0;
      font-size: 18px;
    }
    
    .insights-list li:before {
      content: "💡";
      position: absolute;
      left: 0;
    }
    
    .recommendations-list li:before {
      content: "→";
      color: ${primaryColor};
      font-weight: bold;
      position: absolute;
      left: 0;
    }
    
    .footer {
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #eee;
      text-align: center;
      color: #999;
      font-size: 12px;
    }
    
    @media print {
      body { padding: 20px; }
      .summary-grid { grid-template-columns: repeat(2, 1fr); }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">
      <div class="logo-icon">${companyName.charAt(0)}</div>
      <span class="company-name">${companyName}</span>
    </div>
    <div class="report-meta">
      <div class="report-type">${fullReport.report_type} Report</div>
      <div class="report-date">Generated: ${formatDate(fullReport.generated_at)}</div>
    </div>
  </div>
  
  <h1>${fullReport.report_type?.charAt(0).toUpperCase() + fullReport.report_type?.slice(1)} Performance Report</h1>
  
  ${fullReport.summary ? `
  <h2>Summary</h2>
  <div class="summary-grid">
    ${Object.entries(fullReport.summary).map(([key, value]) => `
      <div class="summary-card">
        <div class="summary-value">${typeof value === 'number' ? value.toLocaleString() : value}</div>
        <div class="summary-label">${key.replace(/_/g, ' ')}</div>
      </div>
    `).join('')}
  </div>
  ` : ''}
  
  ${fullReport.period ? `
  <div class="period-box">
    <div class="period-label">Report Period</div>
    <div class="period-value">${formatDate(fullReport.period.start)} — ${formatDate(fullReport.period.end)}</div>
  </div>
  ` : ''}
  
  ${fullReport.campaigns && fullReport.campaigns.length > 0 ? `
  <h2>Campaigns</h2>
  ${fullReport.campaigns.map(campaign => `
    <div class="campaign-card">
      <div class="campaign-header">
        <span class="campaign-name">${campaign.name}</span>
        <div class="platform-badges">
          ${(campaign.platforms || []).map(p => `<span class="platform-badge">${p}</span>`).join('')}
        </div>
      </div>
      <div class="metrics-row">
        <span class="metric"><span class="metric-label">Impressions:</span> <span class="metric-value">${(campaign.impressions || 0).toLocaleString()}</span></span>
        <span class="metric"><span class="metric-label">Engagements:</span> <span class="metric-value">${(campaign.engagements || 0).toLocaleString()}</span></span>
      </div>
    </div>
  `).join('')}
  ` : ''}
  
  ${fullReport.metrics ? `
  <h2>Key Metrics</h2>
  <div class="summary-grid">
    ${Object.entries(fullReport.metrics)
      .filter(([key, value]) => typeof value !== 'object' || value === null)
      .map(([key, value]) => `
      <div class="summary-card">
        <div class="summary-value">${value === null ? '-' : (typeof value === 'number' ? value.toLocaleString() : value)}</div>
        <div class="summary-label">${key.replace(/_/g, ' ')}</div>
      </div>
    `).join('')}
  </div>
  ` : ''}
  
  ${fullReport.highlights && fullReport.highlights.length > 0 ? `
  <h2>Highlights</h2>
  <ul class="highlights-list">
    ${fullReport.highlights.map(h => `<li>${h}</li>`).join('')}
  </ul>
  ` : ''}
  
  ${fullReport.insights && Array.isArray(fullReport.insights) && fullReport.insights.length > 0 ? `
  <h2>Insights</h2>
  <ul class="insights-list">
    ${fullReport.insights.map(i => `<li>${i}</li>`).join('')}
  </ul>
  ` : ''}
  
  ${fullReport.recommendations && fullReport.recommendations.length > 0 ? `
  <h2>Recommendations</h2>
  <ul class="recommendations-list">
    ${fullReport.recommendations.map(r => `<li>${r}</li>`).join('')}
  </ul>
  ` : ''}
  
  <div class="footer">
    <p>Report generated by ${companyName} • ${formatDate(fullReport.generated_at)}</p>
  </div>
</body>
</html>`;

      // Create blob and download as HTML (can be printed to PDF)
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fullReport.report_type}_report_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Report downloaded! Open the HTML file and print to PDF for best results.");
    } catch (error) {
      console.error("Failed to download report:", error);
      toast.error("Failed to download report");
    }
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case "campaign": return <FileBarChart className="w-4 h-4" />;
      case "weekly": return <Calendar className="w-4 h-4" />;
      case "monthly": return <TrendingUp className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getReportTypeBadge = (type) => {
    switch (type) {
      case "campaign": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "weekly": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "monthly": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generate Report Card */}
      <Card className="glass-card border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Generate Report</CardTitle>
                <CardDescription>Create white-label reports with your branding</CardDescription>
              </div>
            </div>
            {branding?.company_name && (
              <Badge variant="outline" className="text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                {branding.company_name}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Report Type Selection */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Report Type</label>
              <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Weekly Report
                    </span>
                  </SelectItem>
                  <SelectItem value="monthly">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Monthly Report
                    </span>
                  </SelectItem>
                  <SelectItem value="campaign">
                    <span className="flex items-center gap-2">
                      <FileBarChart className="w-4 h-4" />
                      Campaign Report
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Selection (only for campaign reports) */}
            {selectedReportType === "campaign" && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Select Campaign</label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="campaign-select">
                    <SelectValue placeholder="Choose a campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.length > 0 ? (
                      campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name || campaign.business_name || `Campaign ${campaign.id.slice(0, 8)}`}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No campaigns available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Generate Button */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">&nbsp;</label>
              <Button 
                onClick={generateReport} 
                disabled={generating || (selectedReportType === "campaign" && !selectedCampaign)}
                className="w-full"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Branding Preview */}
          {branding && (
            <div className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: branding.primary_color + '20', color: branding.primary_color }}
              >
                {branding.company_name?.charAt(0) || 'Y'}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{branding.company_name}</p>
                <p className="text-xs text-muted-foreground">Reports will use your branding settings</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => window.location.href = '/settings'}>
                Edit Branding
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card className="glass-card border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              Generated Reports
              <Badge variant="outline" className="text-xs ml-2">
                {reports.length} reports
              </Badge>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {reports.length > 0 ? (
            <div className="space-y-3">
              {reports.map((report) => (
                <motion.div
                  key={report.report_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-white/10">
                        {getReportTypeIcon(report.report_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm capitalize">
                            {report.report_type} Report
                          </p>
                          <Badge variant="outline" className={`text-xs ${getReportTypeBadge(report.report_type)}`}>
                            {report.report_type}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Generated: {formatDate(report.generated_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewReport(report.report_id)}
                            data-testid={`view-report-${report.report_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="capitalize">{report.report_type} Report</DialogTitle>
                            <DialogDescription>
                              Generated on {formatDate(report.generated_at)}
                            </DialogDescription>
                          </DialogHeader>
                          {viewingReport && (
                            <div className="space-y-4 mt-4">
                              {/* Summary Metrics */}
                              {viewingReport.summary && typeof viewingReport.summary === 'object' && (
                                <div>
                                  <h4 className="font-medium mb-2">Summary</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {Object.entries(viewingReport.summary).map(([key, value]) => (
                                      <div key={key} className="p-3 rounded-lg bg-white/5 text-center">
                                        <p className="text-lg font-bold text-primary">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                                        <p className="text-xs text-muted-foreground capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Period */}
                              {viewingReport.period && (
                                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                                  <h4 className="font-medium mb-2">Report Period</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {formatDate(viewingReport.period.start)} - {formatDate(viewingReport.period.end)}
                                  </p>
                                </div>
                              )}

                              {/* Campaigns */}
                              {viewingReport.campaigns && viewingReport.campaigns.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2">Campaigns</h4>
                                  <div className="space-y-2">
                                    {viewingReport.campaigns.map((campaign, i) => (
                                      <div key={i} className="p-4 rounded-lg bg-white/5 border border-white/10">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="font-medium">{campaign.name}</p>
                                          <div className="flex gap-1">
                                            {campaign.platforms?.map((platform, j) => (
                                              <Badge key={j} variant="outline" className="text-xs capitalize">
                                                {platform}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                          <div>
                                            <span className="text-muted-foreground">Impressions: </span>
                                            <span className="text-primary font-medium">{campaign.impressions?.toLocaleString() || 0}</span>
                                          </div>
                                          <div>
                                            <span className="text-muted-foreground">Engagements: </span>
                                            <span className="text-primary font-medium">{campaign.engagements?.toLocaleString() || 0}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Highlights */}
                              {viewingReport.highlights && viewingReport.highlights.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2">Highlights</h4>
                                  <ul className="space-y-2">
                                    {viewingReport.highlights.map((highlight, i) => (
                                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-primary">•</span>
                                        {highlight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Metrics (for monthly/campaign reports) */}
                              {viewingReport.metrics && (
                                <div>
                                  <h4 className="font-medium mb-2">Key Metrics</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {Object.entries(viewingReport.metrics)
                                      .filter(([key, value]) => typeof value !== 'object' || value === null)
                                      .map(([key, value]) => (
                                      <div key={key} className="p-3 rounded-lg bg-white/5 text-center">
                                        <p className="text-lg font-bold text-primary">
                                          {value === null ? '-' : (typeof value === 'number' ? value.toLocaleString() : value)}
                                        </p>
                                        <p className="text-xs text-muted-foreground capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Platform Breakdown if exists */}
                                  {viewingReport.metrics.platform_breakdown && (
                                    <div className="mt-4">
                                      <h4 className="font-medium mb-2">Platform Breakdown</h4>
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Object.entries(viewingReport.metrics.platform_breakdown).map(([platform, data]) => (
                                          <div key={platform} className="p-3 rounded-lg bg-white/5">
                                            <p className="font-medium capitalize text-sm mb-1">{platform}</p>
                                            <div className="flex justify-between text-xs text-muted-foreground">
                                              <span>Impressions: <span className="text-primary">{data?.impressions?.toLocaleString() || 0}</span></span>
                                              <span>Engagements: <span className="text-primary">{data?.engagements?.toLocaleString() || 0}</span></span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Comparison (for monthly reports) */}
                              {viewingReport.comparison && (
                                <div>
                                  <h4 className="font-medium mb-2">Month-over-Month Comparison</h4>
                                  <div className="grid grid-cols-3 gap-3">
                                    {Object.entries(viewingReport.comparison).map(([key, value]) => (
                                      <div key={key} className="p-3 rounded-lg bg-white/5 text-center">
                                        <p className={`text-lg font-bold ${value?.startsWith('+') ? 'text-green-400' : value?.startsWith('-') ? 'text-red-400' : 'text-primary'}`}>
                                          {value || '-'}
                                        </p>
                                        <p className="text-xs text-muted-foreground capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Insights (for monthly reports) */}
                              {viewingReport.insights && Array.isArray(viewingReport.insights) && viewingReport.insights.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2">Insights</h4>
                                  <ul className="space-y-2">
                                    {viewingReport.insights.map((insight, i) => (
                                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-primary">💡</span>
                                        {insight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Sections (for older format) */}
                              {viewingReport.sections && Array.isArray(viewingReport.sections) && viewingReport.sections.map((section, i) => (
                                <div key={i} className="p-4 rounded-lg bg-white/5 border border-white/10">
                                  <h4 className="font-medium mb-2">{section.title}</h4>
                                  <p className="text-sm text-muted-foreground">{section.content}</p>
                                </div>
                              ))}

                              {/* Recommendations */}
                              {viewingReport.recommendations && viewingReport.recommendations.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2">Recommendations</h4>
                                  <ul className="space-y-2">
                                    {viewingReport.recommendations.map((rec, i) => (
                                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-primary">•</span>
                                        {rec}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadReport(report)}
                        data-testid={`download-report-${report.report_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteReport(report.report_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-report-${report.report_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No reports generated yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Generate your first report using the form above
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
